import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  listMyVerifications,
  submitDoctorVerification,
} from "@/api/authApi";
import { PageHeader } from "@/components/common/PageHeader";

export default function DoctorVerificationPage() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["my-verifications"],
    queryFn: listMyVerifications,
  });
  const [regNo, setRegNo] = useState("");
  const [docsRaw, setDocsRaw] = useState("");

  const submit = useMutation({
    mutationFn: () =>
      submitDoctorVerification({
        registrationNumber: regNo,
        documents: docsRaw
          .split("\n")
          .map((l) => l.trim())
          .filter(Boolean)
          .map((url) => ({ url, name: url.split("/").pop() ?? url })),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-verifications"] });
      setRegNo("");
      setDocsRaw("");
    },
  });

  const current = (data ?? [])[0];

  return (
    <Box>
      <PageHeader
        title="Verification"
        subtitle="Submit your registration and documents. Our admin team verifies within 2 business days."
      />

      {current && (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1}
          >
            <Box>
              <Typography fontWeight={700}>Latest submission</Typography>
              <Typography variant="body2" color="text.secondary">
                Reg. no.: {current.registrationNumber}
              </Typography>
            </Box>
            <Chip
              label={current.status}
              color={
                current.status === "APPROVED"
                  ? "success"
                  : current.status === "REJECTED"
                    ? "error"
                    : "warning"
              }
              sx={{ fontWeight: 700 }}
            />
          </Stack>
          {current.status === "REJECTED" && current.reason && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Reason: {current.reason}
            </Alert>
          )}
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
        <Stack spacing={2}>
          <TextField
            label="Medical registration number"
            value={regNo}
            onChange={(e) => setRegNo(e.target.value)}
            required
          />
          <TextField
            label="Document URLs (one per line)"
            helperText="Paste links to your degree, license and ID proof. Uploading from disk will be supported soon."
            multiline
            rows={5}
            value={docsRaw}
            onChange={(e) => setDocsRaw(e.target.value)}
          />
          <Box>
            <Button
              variant="contained"
              disabled={!regNo || submit.isPending}
              onClick={() => submit.mutate()}
            >
              {submit.isPending ? "Submitting…" : "Submit for verification"}
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}
